import BN from 'bn.js';
import { parseNearAmount, formatNearAmount } from '@near-js/utils';

import { parseTokenAmount, formatTokenAmount , removeTrailingZeros } from 'utils/amounts';

import config from 'config';
const {
  OPN_CONTRACT,
  OCC_CONTRACT,
  NFT_CONTRACT,
  COLLATERAL_CONTRACT,
  FT_TRANSFER_GAS,
  TOKEN_TRANSFER_DEPOSIT,
  FT_STORAGE_DEPOSIT_GAS,
  FT_MINIMUM_STORAGE_BALANCE,
  OCC_STORAGE_BALANCE,
  MAX_GAS,
  ONE_NEAR_DEPOSIT,
  SEND_NEAR_GAS,
  METHODS,
  FT_MINIMUM_STORAGE_BALANCE_LARGE,
  USDC_CONTRACT,
  FT_MAXIMUM_STORAGE_BALANCE_LARGE
} = config;


import { wallet, createFunctionCallAction } from 'utils/wallet';
import { getTotalGasFee } from 'utils/gasPrice';
import { decodeB64, isFungibleToken, isNonFungibleToken } from 'utils';

const functionCall = (
  methodName: string,
  args: object,
  gas: string | BN | number,
  deposit: string | BN | number
) => createFunctionCallAction(methodName, args, gas, deposit);

export default class FungibleTokens {
  static viewFunctionAccount = wallet.getAccountBasic.call(wallet, 'dontcare');

  static getParsedTokenAmount(
    amount: string,
    symbol: string,
    decimals: number
  ) {
    const parsedTokenAmount =
      symbol === 'NEAR'
        ? parseNearAmount(amount)
        : parseTokenAmount(amount, decimals);

    return parsedTokenAmount;
  }

  static getFormattedTokenAmount(
    amount: string,
    symbol: string,
    decimals?: number
  ) {
    const formattedTokenAmount =
      symbol === 'NEAR'
        ? formatNearAmount(amount, 5)
        : removeTrailingZeros(formatTokenAmount(amount, decimals, 5));
    return formattedTokenAmount;
  }

  static getUniqueTokenIdentity(token) {
    return token.contractName || token.onChainFTMetadata?.symbol;
  }

  static async getUserCollateralProjects({
    accountId,
    from,
    limit,
  } : {
    accountId: string,
    from: number,
    limit: number,
  }) {
    const viewFunctionAccount = await this.viewFunctionAccount;
    return viewFunctionAccount.viewFunctionV1(
      COLLATERAL_CONTRACT,
      METHODS.get_user_projects,
      {
        account_id: accountId,
        from,
        limit,
      }
    );
  };

  static async getFtMetadata(contractName?: string) {
    const viewFunctionAccount = await this.viewFunctionAccount;
    return viewFunctionAccount.viewFunctionV1(
      contractName||OPN_CONTRACT ,
      METHODS.ft_metadata,
    );
  }

  static async getMtMetadataBase({ metadataIds }: { metadataIds: string[] }) {
    const viewFunctionAccount = await this.viewFunctionAccount;
    return viewFunctionAccount.viewFunctionV1(
      OCC_CONTRACT,
      METHODS.mt_metadata_base_by_metadata_id,
      { base_metadata_ids: metadataIds }
    );
  }

  static async getProfileBalances({
    accountId,
    fromIndex,
    limit,
  } : {
    accountId: string,
    fromIndex: string,
    limit: string,
  }) {
    const viewFunctionAccount = await this.viewFunctionAccount;
    const balances = await viewFunctionAccount.viewFunctionV1(
      OCC_CONTRACT,
      METHODS.get_profile_balances,
      {
        account_id: accountId,
        from_index: fromIndex,
        limit,
      }
    );
    const ftBalances = balances.filter(([tokenKey]) => isFungibleToken(tokenKey));
    const nftBalances = balances.filter(([tokenKey]) => isNonFungibleToken(tokenKey));
    return { ftBalances, nftBalances, numOfBalances: balances.length }
  }

  static async getFtBalances({ accountId } : { accountId: string }) {
    let isPending = true;
    let fromIndex = 0;
    const limit = 10;
    const ftBalances = [];

    while (isPending) {
      const { ftBalances: ftBalancesChunk, numOfBalances } = await this.getProfileBalances({
        accountId,
        fromIndex: fromIndex.toString(),
        limit: limit.toString(),
      });

      isPending = numOfBalances === limit;
      fromIndex += limit;
      ftBalances.push(...ftBalancesChunk);
    }

    return ftBalances;
  }

  static async getProjectStage(arrayOfProjectsId) {
    const viewFunctionAccount = await this.viewFunctionAccount;
    return arrayOfProjectsId.map(({ projectId, projectStageId }) =>
      viewFunctionAccount.viewFunctionV1(
        NFT_CONTRACT,
        METHODS.get_collateral_stage,
        {
          project_id: projectId.toString(),
          stage_id: projectStageId.toString()
        }
      )
    );
  }

  static async getViewMethod({
    contractName,
    accountId,
    methodName
  }: {
    contractName: string;
    accountId: string;
    methodName: string;
  }) {
    const viewFunctionAccount = await this.viewFunctionAccount;
    return viewFunctionAccount.viewFunctionV1(contractName, methodName, {
      account_id: accountId
    });
  }

  async getStorageBalance({
    contractName,
    accountId
  }: {
    contractName: string;
    accountId: string;
  }) {
    const constructor = this.constructor as typeof FungibleTokens;
    return await constructor.getViewMethod({
      contractName,
      accountId,
      methodName: METHODS.storage_balance_of
    });
  };

  async isStorageBalanceAvailable({
    contractName,
    accountId
  }: {
    contractName: string;
    accountId: string;
  }) {
    const constructor = this.constructor as typeof FungibleTokens;
    const storageBalance = await constructor.getViewMethod({
      contractName,
      accountId,
      methodName: METHODS.storage_balance_of
    });
    return storageBalance?.total !== undefined;
  }

  async getEstimatedTotalFees({
    accountId,
    contractName
  }: {
    accountId?: string;
    contractName?: string;
  } = {}) {
    if (
      contractName &&
      accountId &&
      !(await this.isStorageBalanceAvailable({ contractName, accountId }))
    ) {
      const totalGasFees = await getTotalGasFee(
        new BN(FT_TRANSFER_GAS).add(new BN(FT_STORAGE_DEPOSIT_GAS))
      );
      return new BN(totalGasFees)
        .add(new BN(FT_MINIMUM_STORAGE_BALANCE))
        .toString();
    } else {
      return getTotalGasFee(contractName ? FT_TRANSFER_GAS : SEND_NEAR_GAS);
    }
  }

  /**
   Transfers a storage deposit from the specified account to the contract with the given name.
   @param {string} accountId - The account ID of the account sending the storage deposit.
   @param {string} contractName - The name of the contract that will receive the storage deposit.
   @param {string} storageDepositAmount - The amount of the storage deposit to transfer.
   @returns {Promise} - A promise that resolves when the transaction is signed and sent.
   */
  async transferStorageDeposit({
    accountId,
    contractName,
    receiverAccount,
    storageDepositAmount,
    registrationOnly,
  }) {
    const account = await wallet.getAccount(accountId);
    return await account.signAndSendTransaction({
      receiverId: contractName,
      actions: [
        functionCall(
          METHODS.storage_deposit,
          {
            // force: true
            account_id: receiverAccount,
            registration_only: registrationOnly
          },
          FT_STORAGE_DEPOSIT_GAS,
          storageDepositAmount
        )
      ]
    });
  }

  async withdrawStorageDeposit({ accountId, contractName, amount }) {
    const account = await wallet.getAccount(accountId);
    return await account.signAndSendTransaction({
      receiverId: contractName,
      actions: [
        functionCall(
          METHODS.storage_withdraw,
          { amount },
          FT_STORAGE_DEPOSIT_GAS,
          TOKEN_TRANSFER_DEPOSIT
        )
      ]
    });
  }

  async getEstimatedTotalNearAmount({ amount }) {
    return new BN(amount)
      .add(new BN(await this.getEstimatedTotalFees()))
      .toString();
  }

  sumRewards(rewards) {
    if (!rewards?.length) return '0';

    return rewards.reduce((prevSum, item) => (
      new BN(prevSum)
        .add(new BN(item.amount))
        .toString()
    ), '0');
  };

  async getPlantingProfile(accountId: string) {
    const constructor = this.constructor as typeof FungibleTokens;
    return await constructor.getViewMethod({
      contractName: OPN_CONTRACT,
      accountId,
      methodName: METHODS.get_planting_profile
    });
  };
  // async getPlantingLockedAmaunt(accountId: string) {
  //   const constructor = this.constructor as typeof FungibleTokens;
  //   return await constructor.getViewMethod({
  //     contractName: OPN_CONTRACT,
  //     accountId,
  //     methodName: METHODS.get_planting_profile
  //   });
  // };

  async getUncollectedPlantingRewards (accountId: string, fromId: string | null) {
    const constructor = this.constructor as typeof FungibleTokens;
    const viewFunctionAccount = await constructor.viewFunctionAccount;
    return await viewFunctionAccount.viewFunctionV1(
      OPN_CONTRACT,
      METHODS.get_uncollected_planting_rewards,
      {
        account_id: accountId,
        from_id: fromId,
        limit: '50',
      }
    );
  };

  async getAllUncollectedPlantingRewards (accountId: string, lastRewardId: string | null) {
    let isPending = !!lastRewardId || lastRewardId===null ;
    let fromId = lastRewardId;
    let uncollectedAmount = '0';
    const uncollectedRewards = [];

    while (isPending) {
      const chunks = await this.getUncollectedPlantingRewards(accountId, fromId);
      const chunksAmount = this.sumRewards(chunks);

      const lastChunk = chunks[chunks.length - 1];
      fromId = lastChunk?.reward_id || null;
      isPending = !!lastChunk?.next_reward_id;
      uncollectedAmount = new BN(uncollectedAmount)
        .add(new BN(chunksAmount))
        .toString();

      uncollectedRewards.push(...chunks);
    }

    return {
      uncollected_rewards: uncollectedRewards,
      uncollected_amount: uncollectedAmount,
    };
  };

  async getCollectedPlantingRewards(accountId: string, fromId: string) {
    const constructor = this.constructor as typeof FungibleTokens;
    const viewFunctionAccount = await constructor.viewFunctionAccount;
    return await viewFunctionAccount.viewFunctionV1(
      OPN_CONTRACT,
      METHODS.get_collected_planting_rewards,
      {
        account_id: accountId,
        contract_id: OCC_CONTRACT,
        from_index: fromId,
        limit: '50',
      }
    );
  }

  async getAllCollectedPlantingRewards(accountId: string) {
    let isPending = true;
    let fromId = '0';
    let collectedAmount = '0';
    const collectedRewards = [];

    while (isPending) {
      const chunks = await this.getCollectedPlantingRewards(accountId, fromId);
      const chunksAmount = this.sumRewards(chunks);
      collectedRewards.push(...chunks);

      isPending = !!chunks.length;
      fromId = collectedRewards.length.toString();
      collectedAmount = new BN(collectedAmount)
        .add(new BN(chunksAmount))
        .toString();
    }

    return {
      collected_rewards: collectedRewards,
      collected_amount: collectedAmount,
    };
  }

  async collectAllPlantingRewards({ accountId }) {
    const account = await wallet.getAccount(accountId);
    const profile = await this.getPlantingProfile(accountId);
    const lastRewardId = profile?.last_reward_id || null;
    const rewards = await this.getAllUncollectedPlantingRewards(accountId, lastRewardId);

    let isPending = new BN(rewards.uncollected_amount).gt(new BN('0'));

    while (isPending) {
      const txState = await account.signAndSendTransaction({
        receiverId: OPN_CONTRACT,
        actions: [
          functionCall(
            METHODS.collect_planting_rewards,
            {},
            MAX_GAS,
            ONE_NEAR_DEPOSIT
          ),
        ],
      });
      const txResult = txState?.status?.SuccessValue || '';

      if (txResult) {
        const collectedRewards = JSON.parse(decodeB64(txResult));
        const lastCollectedReward = collectedRewards.slice(-1);
        isPending = !!lastCollectedReward.next_reward_id;
      } else isPending = false;
    }
  }

  async withdrawAllPlantingRewards({ accountId }) {
    const account = await wallet.getAccount(accountId);
    const rewards = await this.getAllCollectedPlantingRewards(accountId);

    let isPending = new BN(rewards.collected_amount).gt(new BN('0'));

    while (isPending) {
      const txState = await account.signAndSendTransaction({
        receiverId: OPN_CONTRACT,
        actions: [
          functionCall(
            METHODS.withdraw_planting_rewards,
            { contract_id: OCC_CONTRACT },
            MAX_GAS,
            TOKEN_TRANSFER_DEPOSIT
          )
        ]
      });
      const txResult = txState?.status?.SuccessValue || '';

      if (txResult) {
        const withdrawnRewards = JSON.parse(decodeB64(txResult));

        if (!withdrawnRewards?.length) {
          isPending = false;
          break;
        }

        const remainingRewards = await this.getAllCollectedPlantingRewards(accountId);
        isPending = new BN(remainingRewards.collected_amount).gt(new BN('0'));
      } else isPending = false;
    }
  }

  async plant({
    accountId,
    contractName,
    amount,
    methodName
  }: {
    accountId: string;
    contractName: string;
    amount: string;
    methodName: string;
  }) {
    const account = await wallet.getAccount(accountId);

    if (methodName === METHODS.plant_tokens || methodName === METHODS.revoke_planted_tokens || methodName === METHODS.withdraw_planted_tokens) {
      const DEPOSIT = methodName === METHODS.plant_tokens ? ONE_NEAR_DEPOSIT : TOKEN_TRANSFER_DEPOSIT;
      return await account.signAndSendTransaction({
        receiverId: contractName,
        actions: [ functionCall(methodName, { amount }, MAX_GAS, DEPOSIT) ]
      });
    }

    if (methodName === METHODS.collect_planting_rewards) {
      return await this.collectAllPlantingRewards({ accountId });
    }
    if (methodName === METHODS.withdraw_planting_rewards) {
      return await this.withdrawAllPlantingRewards({ accountId });
    }
  }

  async retire({
    accountId,
    contractName,
    selected,
    recipient
  }: {
    accountId: string;
    contractName: string;
    selected: string[];
    recipient: string;
  }) {
    const account = await wallet.getAccount(accountId);
    return await account.signAndSendTransaction({
      receiverId: contractName,
      actions: [
        functionCall(
          METHODS.mt_offset,
          {
            token_ids: selected.map((item) => `ft:${item[0]}`),
            amounts: selected.map((item) => item[1]),
            title: recipient,
          },
          FT_STORAGE_DEPOSIT_GAS,
          OCC_STORAGE_BALANCE
        )
      ]
    });
  }

  /**
   * This function transfers tokens or money from the user's account to another account or contract.
   * @param {string} accountId - The user's account ID.
   * @param {string} contractName - The name of the contract to transfer tokens to. If null, transfer money instead of tokens.
   * @param {string} amount - The amount of tokens or money to transfer.
   * @param {Array} selected - The list of project IDs and amounts to transfer (in case contractName is OCC_CONTRACT).
   * @param {string} receiverId - The account ID of the receiver.
   * @param {string} memo - An optional memo message for the transfer.
   * @returns {Promise<object>} - The result of the transaction.
   */
  async transfer({
    accountId,
    contractName,
    amount,
    selected,
    receiverId,
    memo = ''
  }: {
    accountId: string;
    contractName: string;
    amount: string;
    selected: string[];
    receiverId: string;
    memo?: string;
  }) {
    // Get the user's account using the getAccount() method from a wallet object.
    const account = await wallet.getAccount(accountId);
    if (contractName) {
      // If contractName is OPN_CONTRACT, call the ft_transfer method from the contract.
      if (contractName === OPN_CONTRACT || contractName === USDC_CONTRACT) {
        return await account.signAndSendTransaction({
          receiverId: contractName,
          actions: [
            functionCall(
              METHODS.ft_transfer,
              {
                amount,
                receiver_id: receiverId,
                memo
              },
              FT_TRANSFER_GAS,
              TOKEN_TRANSFER_DEPOSIT
            )
          ]
        });
      }

      // If contractName is OCC_CONTRACT, call the mt_batch_transfer method from the contract.
      if (contractName === OCC_CONTRACT) {
        return await account.signAndSendTransaction({
          receiverId: contractName,
          actions: [
            functionCall(
              METHODS.mt_batch_transfer,
              {
                receiver_id: receiverId,
                token_ids: selected.map((item) => `ft:${item[0]}`),
                amounts: selected.map((item) => item[1]),
                memo
              },
              FT_TRANSFER_GAS,
              TOKEN_TRANSFER_DEPOSIT
            )
          ]
        });
      }
    } else {
      // If contractName is null, send money using the sendMoney() method from the account object.
      return await account.sendMoney(receiverId, amount);
    }
  }

  async transferAndStorage({
                   accountId,
                   contractName,
                   amount,
                   selected,
                   receiverId,
                   memo = ''
                 }: {
    accountId: string;
    contractName: string;
    amount: string;
    selected: string[];
    receiverId: string;
    memo?: string;
  }) {
    // Get the user's account using the getAccount() method from a wallet object.
    const account = await wallet.getAccount(accountId);


    if (contractName) {
      // If contractName is OPN_CONTRACT, call the ft_transfer method from the contract.
      const depositStorage =contractName === OCC_CONTRACT? FT_MAXIMUM_STORAGE_BALANCE_LARGE:FT_MINIMUM_STORAGE_BALANCE_LARGE
      const actions = [    functionCall(
          METHODS.storage_deposit,
          {
            account_id: receiverId,
            registration_only: false
          },
          FT_TRANSFER_GAS,
          depositStorage
      )];
      if (contractName === OPN_CONTRACT || contractName === USDC_CONTRACT) {
        actions.push( functionCall(
                METHODS.ft_transfer,
                {
                  amount,
                  receiver_id: receiverId,
                  memo
                },
                FT_TRANSFER_GAS,
                TOKEN_TRANSFER_DEPOSIT
            )
        )

      }

      // If contractName is OCC_CONTRACT, call the mt_batch_transfer method from the contract.
      if (contractName === OCC_CONTRACT) {
           actions.push( functionCall(
                METHODS.mt_batch_transfer,
                {
                  receiver_id: receiverId,
                  token_ids: selected.map((item) => `ft:${item[0]}`),
                  amounts: selected.map((item) => item[1]),
                  memo
                },
                FT_TRANSFER_GAS,
                TOKEN_TRANSFER_DEPOSIT
            ))

      }
      return await account.signAndSendTransaction({
        receiverId: contractName,
        actions
      });
    } else {
      // If contractName is null, send money using the sendMoney() method from the account object.
      return await account.sendMoney(receiverId, amount);
    }
  }
}


export const fungibleTokensService = new FungibleTokens();
